<template>
  <div class="domain-event-card" :style="{ minHeight: cardHeight } ">
    <div
      class="card-item"
      :style="{ 'border-color': cardColor }"
    >
      <div class="d-flex align-center mb-1">
        <quick-rename-input
          :value="schema.name"
          @onChange="onBlurName"
          density="compact"
          :max-length="30"
          class="flex-grow-1 font-weight-medium"
          :disabled="isWorkflowDisabled"
        />
        <v-btn
          v-if="!isWorkflowDisabled && !hideDelete"
          size="small"
          width="20"
          min-width="20"
          height="20"
          variant="plain"
          class="ml-2 pa-0"
          :loading="deleting"
          @click="emit('deleteSchema', schema.id)"
        >
          <v-icon size="14">mdi-trash-can-outline</v-icon>
        </v-btn>
      </div>

      <div class="mb-1 entity-select-wrapper">
        <select-entity
          :entityId="schema.entityId"
          @onSelectEntity="onSelectEntity"
        >
          <v-chip
            density="compact"
            class="cursor-pointer entity-chip"
            :disabled="isWorkflowDisabled && !linkedEntity"
          >
            <v-icon start size="12">mdi-database</v-icon>
            {{ linkedEntity ? linkedEntity.name : 'Select Entity' }}
          </v-chip>
        </select-entity>
      </div>

      <div
        v-if="(schema.fields ?? []).length"
        class="py-1 fields"
        ref="fieldsRef"
      >
        <template v-for="(field, fieldIndex) in (schema.fields ?? [])" :key="fieldIndex">
          <div class="sortableRow">
            <div class="field-name d-flex align-center">
              <v-btn
                class="border-none cursor-handle sortHandle"
                size="small"
                variant="plain"
                tabindex="-1"
                icon="mds:drag-indicator"
                :disabled="isWorkflowDisabled"
              />
              <quick-rename-input
                :value="field.name"
                @onChange="(e) => onBlurField(e, fieldIndex)"
                density="compact"
                :max-length="30"
                class="flex-grow-1"
                :disabled="isWorkflowDisabled"
              />
              <v-chip
                v-if="field.relatedEntityId && getRelatedEntity(field)"
                density="compact"
                size="small"
                :closable="!isWorkflowDisabled"
                class="related-entity-chip px-2 ml-1"
              >
                {{
                  field.cardinality === 'one-to-many'
                    ? `[ ${getRelatedEntity(field)?.name} ]`
                    : `${getRelatedEntity(field)?.name}`
                }}
                <template v-if="!isWorkflowDisabled" #close>
                  <v-icon
                    icon="mdi-close-circle"
                    color="#888"
                    :size="16"
                    class="remove-entity-icon"
                    @click.stop="selectRelatedEntity(null, schema.id, fieldIndex)"
                  />
                </template>
              </v-chip>
              <v-menu v-if="!isWorkflowDisabled" class="card-menu">
                <template v-slot:activator="{ props }">
                  <v-btn
                    icon="mdi-dots-vertical"
                    class="domain-event-field-actions-btn"
                    size="small"
                    variant="plain"
                    v-bind="props"
                  />
                </template>
                <v-list density="compact">
                  <select-entity
                    @onSelectEntity="(entity) => selectRelatedEntity(entity, schema.id, fieldIndex)"
                  >
                    <v-list-item>
                      <v-list-item-title class="cursor-pointer">
                        <v-icon class="mr-2" color="grey">mdi-database</v-icon>
                        Select Entity
                      </v-list-item-title>
                    </v-list-item>
                  </select-entity>
                  <v-list-item @click="deleteSchemaField(schema.id, fieldIndex)">
                    <v-list-item-title>
                      <v-icon class="mr-2" color="grey" icon="mdi-trash-can-outline" />
                      Delete
                    </v-list-item-title>
                  </v-list-item>
                  <v-divider></v-divider>
                  <div class="d-flex justify-center pt-1">
                    <toggle
                      mandatory
                      :value="field.cardinality || 'one-to-one'"
                      :options="FIELD_CARDINALITY_OPTIONS"
                      :disabled="!field.relatedEntityId"
                      @update:modelValue="(cardinality) => setFieldCardinality(schema.id, fieldIndex, cardinality)"
                    />
                  </div>
                </v-list>
              </v-menu>
            </div>
            <!-- Sub-fields for related entities -->
            <div v-if="field.relatedEntityId" ref="subFieldsRef">
              <div
                v-for="(item, subIndex) in field.fields"
                :key="subIndex"
                class="pl-4 field-name d-flex align-center sortableRow"
              >
                <v-btn
                  class="border-none cursor-handle sortHandle"
                  size="small"
                  variant="plain"
                  tabindex="-1"
                  icon="mds:drag-indicator"
                  :disabled="isWorkflowDisabled"
                />
                <quick-rename-input
                  :value="item.name"
                  @onChange="(e) => onBlurSubField(e, fieldIndex, subIndex)"
                  density="compact"
                  :max-length="30"
                  class="flex-grow-1"
                  :disabled="isWorkflowDisabled"
                />
                <v-progress-circular
                  v-if="deletingSubField === `${fieldIndex}-${subIndex}`"
                  :size="14"
                  :width="2"
                  indeterminate
                  color="grey-darken-1"
                />
                <v-icon
                  v-else-if="!isWorkflowDisabled"
                  icon="mdi-trash-can-outline"
                  class="domain-event-field-actions-btn cursor-pointer"
                  size="14"
                  color="grey-darken-1"
                  @click="onRemoveSubField(schema.id, fieldIndex, subIndex)"
                />
              </div>
              <div v-if="!isWorkflowDisabled" class="d-flex align-center pl-4 btn-add-sub-field">
                <v-tooltip location="bottom" open-delay="100">
                  <template v-slot:activator="{ on, props }">
                    <v-btn
                      v-bind="props"
                      v-on="{ ...on }"
                      size="small"
                      variant="plain"
                      @mousedown="addSchemaSubField(schema.id, fieldIndex)"
                      tabindex="-1"
                      icon="mdi-plus"
                    />
                  </template>
                  <span>Add field</span>
                </v-tooltip>
              </div>
            </div>
          </div>
        </template>
      </div>

      <div
        class="d-flex align-center"
        :class="{
          'justify-space-between': usageStatus === 'unused',
          'justify-start': usageStatus !== 'unused',
          'mb-2': linkedReadModels.length
        }"
      >
        <v-tooltip v-if="!isWorkflowDisabled" location="bottom" open-delay="100">
          <template v-slot:activator="{ on, props }">
            <v-btn
              v-bind="props"
              v-on="{ ...on }"
              size="small"
              width="16"
              min-width="16"
              height="16"
              variant="plain"
              @click="onAddField"
              tabindex="-1"
              class="pa-0"
            >
              <v-icon size="16">mdi-plus</v-icon>
            </v-btn>
          </template>
          <span>Add field</span>
        </v-tooltip>
        <v-spacer v-if="usageStatus === 'unused'" />
        <v-tooltip v-if="usageStatus === 'unused'" location="bottom" open-delay="300">
          <template v-slot:activator="{ props }">
            <div v-bind="props" class="unused-badge">
              <v-icon size="12" class="mr-1">mdi-alert-circle-outline</v-icon>
              <span>Unused</span>
            </div>
          </template>
          <span>Not used by any card in the domain model</span>
        </v-tooltip>
      </div>

      <div v-if="linkedReadModels.length" class="read-model-links">
        <div class="text-caption text-grey-darken-1 mb-1">Updates projections:</div>
        <div class="d-flex flex-wrap ga-1">
          <v-chip
            v-for="rm in linkedReadModels"
            :key="rm.id"
            density="compact"
            size="small"
            variant="flat"
            class="read-model-chip"
            :style="{ backgroundColor: readModelBgColor, color: 'var(--text-dark)' }"
          >
            <v-icon start size="12">mdi-eye-outline</v-icon>
            {{ rm.name }}
          </v-chip>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue'
import { useStore } from 'vuex'
import QuickRenameInput from '@/components/QuickRenameInput'
import SelectEntity from '@/components/SelectEntity.vue'
import Toggle from '@/components/Toggle.vue'
import useDomainEventActions from '@/composables/useDomainEventActions'
import useDomainModel from '@/composables/useDomainModel'
import useReadModelDomainEvents from '@/composables/useReadModelDomainEvents'
import { SchemaType, FIELD_CARDINALITY_OPTIONS } from '@/constants/schema'
import { diagramHelpers } from '@/mixins/diagramHelpers'

const { schema, usageStatus, deleting, hideDelete } = defineProps({
  schema: Object,
  usageStatus: String,
  deleting: Boolean,
  hideDelete: Boolean
})
const emit = defineEmits(['deleteSchema'])

const deletingSubField = ref(null)

const { blurDomainEventName } = useDomainEventActions()
const { getReadModelsForDomainEvent } = useReadModelDomainEvents()
const {
  cardTypeColor,
  getDomainEventCardHeight,
  selectSourceEntity,
  selectRelatedEntity,
  setFieldCardinality,
  deleteSchemaField,
  renameSchemaField,
  renameSchemaSubField,
  addSchemaField,
  addSchemaSubField,
  removeSchemaSubField
} = useDomainModel()

const store = useStore()
const workflow = computed(() => store.state.workflow.workflow)
const isWorkflowDisabled = computed(() => store.state.isWorkflowDisabled)
const cardTypes = computed(() => store.state.cardTypes.cardTypes)
const domainEventCardType = computed(() => diagramHelpers.methods.getDomainEventCardType(cardTypes.value))
const queryCardType = computed(() => diagramHelpers.methods.getQueryCardType(cardTypes.value))
const cardColor = computed(() => cardTypeColor(domainEventCardType.value) || '#eeeeee')
const readModelBgColor = computed(() => {
  const c = queryCardType.value?.color
  return c ? `rgba(${c.red},${c.green},${c.blue},0.15)` : 'transparent'
})
const cardHeight = computed(() => getDomainEventCardHeight(schema, linkedReadModels))

const onRemoveSubField = async (schemaId, fieldIndex, subIndex) => {
  deletingSubField.value = `${fieldIndex}-${subIndex}`
  try {
    await removeSchemaSubField(schemaId, fieldIndex, subIndex)
  } finally {
    deletingSubField.value = null
  }
}

const linkedEntity = computed(() => {
  if (!schema.entityId) {
    return null
  }

  return (workflow.value.schemas ?? []).find((s) => s.id === schema.entityId && s.type === SchemaType.ENTITY)
})

const linkedReadModels = computed(() => getReadModelsForDomainEvent(schema))

const getRelatedEntity = (field) => {
  if (!field.relatedEntityId) {
    return null
  }

  return (workflow.value.schemas ?? []).find((s) => s.id === field.relatedEntityId)
}

const onAddField = (e) => {
  addSchemaField(schema.id)
  e.target.closest('button')?.blur()
}

const onBlurName = (e) => {
  blurDomainEventName(e, schema.id)
}

const onSelectEntity = (entity) => {
  selectSourceEntity(schema.id, entity?.id || null)
}

const onBlurField = (e, fieldIndex) => {
  const newName = e.target.innerText?.trim() ?? ''
  renameSchemaField(schema.id, fieldIndex, newName)
}

const onBlurSubField = (e, fieldIndex, subFieldIndex) => {
  const newName = e.target.innerText?.trim() ?? ''
  renameSchemaSubField(schema.id, fieldIndex, subFieldIndex, newName)
}
</script>

<style scoped>
.card-item {
  min-width: 200px;
  max-width: 300px;
  overflow: hidden;
  padding: 12px;
  border-radius: 8px;
  background: var(--light-1);
  border: 1px solid black;
  border-top-width: 2px;
  box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.15);
  line-height: 1;
}

.entity-select-wrapper {
  font-size: 0;
}

:deep(.entity-chip.v-chip) {
  height: 16px;
  font-size: 11px;
}

:deep(.read-model-chip.v-chip) {
  height: 16px;
  font-size: 11px;
}

.field-name {
  font-size: 13px;
}

.cursor-handle {
  cursor: grab;
}

.sortHandle {
  margin-left: -4px;
}

.domain-event-field-actions-btn {
  opacity: 0;
  transition: opacity 0.2s;
}

.sortableRow:hover .domain-event-field-actions-btn {
  opacity: 0.7;
}

.sortableRow:hover .domain-event-field-actions-btn:hover {
  opacity: 1;
}

.btn-add-sub-field {
  opacity: 0;
  height: 0;
  transition: all 0.2s;
}

.sortableRow:hover .btn-add-sub-field {
  opacity: 1;
  height: 16px;
}

.sortableRow:hover .btn-add-sub-field .v-btn {
  opacity: 0.7;
}

.sortableRow:hover .btn-add-sub-field .v-btn:hover {
  opacity: 1;
}

.unused-badge {
  display: inline-flex;
  align-items: center;
  padding: 0 8px;
  border-radius: 12px;
  font-size: 11px;
  white-space: nowrap;
  background-color: #FFF3E0;
  color: #E65100;
  height: 16px;
}

.text-caption {
  font-size: 11px;
  line-height: 18px;
}

:deep(.related-entity-chip) {
  position: relative;

  &&& .v-chip__close {
    margin-inline-start: 0;
    margin-inline-end: 0;
    position: absolute;
    right: 0;
    background: #e8e8e8;
    opacity: 0;
    transition: all 0.2s;
  }

  &:hover {
    &&& .v-chip__close {
      opacity: 1;
    }
  }
}
</style>
